import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export function NavItem({
  to,
  icon: Icon,
  children,
  end,
  activeSearch,
  sub,
  badge,
}: {
  to: string;
  icon: LucideIcon;
  children: React.ReactNode;
  end?: boolean;
  activeSearch?: string;
  sub?: boolean;
  /** Pending-items count; renders a pill after the label when > 0. */
  badge?: number;
}) {
  const location = useLocation();
  return (
    <li>
      <NavLink
        to={to}
        end={end}
        title={typeof children === 'string' ? children : undefined}
        className={({ isActive }) => {
          const base = sub ? 'nav-sub-item' : 'nav-item';
          if (!isActive) return base;
          if (activeSearch && !location.search.includes(activeSearch)) return base;
          return `${base} active`;
        }}
      >
        {!sub && <Icon className="nav-item-icon" size={15} strokeWidth={1.75} />}
        <span className="nav-item-label">{children}</span>
        {typeof badge === 'number' && badge > 0 && (
          <span className="nav-badge">{badge > 99 ? '99+' : badge}</span>
        )}
      </NavLink>
    </li>
  );
}

/** Nested sub-group inside a dropdown section. Manages its own open state. */
export function NavSubDropdown({
  label,
  icon: Icon,
  isActive,
  children,
}: {
  label: string;
  icon: LucideIcon;
  isActive: boolean;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(isActive);

  useEffect(() => {
    if (isActive) setIsOpen(true);
  }, [isActive]);

  return (
    <li className="sub-dropdown-section">
      <button
        className={`sub-dropdown-toggle${isActive ? ' active' : ''}`}
        onClick={() => setIsOpen(o => !o)}
      >
        <Icon className="sub-dropdown-icon" size={13} strokeWidth={1.75} />
        <span className="sub-dropdown-label">{label}</span>
        <span className="dropdown-chevron">
          {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </span>
      </button>
      {isOpen && <ul className="sub-dropdown-menu">{children}</ul>}
    </li>
  );
}

export function NavDropdown({
  label,
  icon: Icon,
  isActive,
  isOpen,
  onToggle,
  badge,
  children,
}: {
  label: string;
  icon?: LucideIcon;
  isActive: boolean;
  isOpen: boolean;
  onToggle: () => void;
  /** Sum of child badges; shown on the toggle so a closed section still signals pending items. */
  badge?: number;
  children: React.ReactNode;
}) {
  return (
    <li className="dropdown-section">
      <button
        className={`dropdown-toggle${isActive ? ' active' : ''}`}
        onClick={onToggle}
        title={label}
      >
        {Icon && <Icon className="dropdown-toggle-icon" size={15} strokeWidth={1.75} />}
        <span className="dropdown-toggle-label">{label}</span>
        {typeof badge === 'number' && badge > 0 && !isOpen && (
          <span className="nav-badge">{badge > 99 ? '99+' : badge}</span>
        )}
        <span className="dropdown-chevron">
          {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </span>
      </button>
      {isOpen && <ul className="dropdown-menu">{children}</ul>}
    </li>
  );
}
